/**
 * Shared utility for building Google Maps InfoWindow HTML content.
 * Used by both OpportunityMap and StoresMapPage marker popups.
 */

import { formatFullAddress } from './addressUtils';

export interface InfoWindowStoreData {
  id?: string | number;
  site?: string;
  brand_name?: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | number | null;
  channel?: string | null;
  customer_id?: string | null;
  distance?: number;
  crime_rate?: number | null;
  purchek_install_date?: string | null;
  containment_install_date?: string | null;
  cart_manager_install_date?: string | null;
  subscription_start_date?: string | null;
  paired_wheel_install_date?: string | null;
  plaza_cm_customer_count?: number | null;
  total_crime_amount_usd?: number | null;
  video_incident_count?: number | null;
  recommendation_rank?: number | null;
  store_number?: string | null;
  updated_store_ids?: Array<{ store_id: string; created_at?: string }> | null;
}

export interface InfoWindowOptions {
  showStreetViewButton?: boolean;
  showNeighbourhoodButton?: boolean;
  recommendedSolutions?: string[];
  whitespaceOpportunities?: string[];
  showExportToggle?: boolean;
  isSelectedForExport?: boolean;
}

export function buildInfoWindowContent(
  store: InfoWindowStoreData,
  options: InfoWindowOptions = {}
): string {
  const {
    showStreetViewButton = false,
    showNeighbourhoodButton = true,
    recommendedSolutions = [],
    whitespaceOpportunities: customWhitespace,
    showExportToggle = false,
    isSelectedForExport = false,
  } = options;

  // Installed solutions
  const installedSolutions: { name: string; date: string | null | undefined }[] = [];
  if (store.purchek_install_date) installedSolutions.push({ name: 'Purchek', date: store.purchek_install_date });
  if (store.containment_install_date) installedSolutions.push({ name: 'Cart Containment', date: store.containment_install_date });
  if (store.cart_manager_install_date) installedSolutions.push({ name: 'Cart Manager', date: store.cart_manager_install_date });
  if (store.subscription_start_date) installedSolutions.push({ name: 'Subscription', date: store.subscription_start_date });
  if (store.paired_wheel_install_date) installedSolutions.push({ name: 'Paired Wheel', date: store.paired_wheel_install_date });

  // Whitespace: use custom if provided, otherwise compute from install dates
  const whitespaceOpportunities = customWhitespace ?? (() => {
    const ws: string[] = [];
    if (!store.purchek_install_date) ws.push('Purchek');
    if (!store.containment_install_date) ws.push('Cart Containment');
    if (!store.cart_manager_install_date) ws.push('Cart Manager');
    if (!store.subscription_start_date) ws.push('Subscription');
    if (!store.paired_wheel_install_date) ws.push('Paired Wheel');
    return ws;
  })();

  // Street view button SVG (360° icon)
  const streetViewBtn = showStreetViewButton && store.id ? `
    <button id="street-view-icon-btn-${store.id}" style="
      background: none;
      border: none;
      cursor: pointer;
      padding: 2px;
      display: flex;
      align-items: center;
      color: #f59e0b;
      transition: color 0.2s;
      font-size: 18px;
    " title="View 360°">
      <svg width="28" height="28" viewBox="0 0 122.88 65.79" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
        <path d="M13.37,31.32c-22.23,12.2,37.65,19.61,51.14,19.49v-7.44l11.21,11.2L64.51,65.79v-6.97 C37.4,59.85-26.41,42.4,11.97,27.92c0.36,1.13,0.8,2.2,1.3,3.2L13.37,31.32L13.37,31.32z M108.36,8.31c0-2.61,0.47-4.44,1.41-5.48 c0.94-1.04,2.37-1.56,4.3-1.56c0.92,0,1.69,0.12,2.28,0.34c0.59,0.23,1.08,0.52,1.45,0.89c0.38,0.36,0.67,0.75,0.89,1.15 c0.22,0.4,0.39,0.87,0.52,1.41c0.26,1.02,0.38,2.09,0.38,3.21c0,2.49-0.42,4.32-1.27,5.47c-0.84,1.15-2.29,1.73-4.36,1.73 c-1.15,0-2.09-0.19-2.8-0.55c-0.71-0.37-1.3-0.91-1.75-1.62c-0.33-0.51-0.59-1.2-0.77-2.07C108.45,10.34,108.36,9.38,108.36,8.31 L108.36,8.31z M26.47,10.49l-9-1.6c0.75-2.86,2.18-5.06,4.31-6.59C23.9,0.77,26.91,0,30.8,0c4.47,0,7.69,0.83,9.69,2.5 c1.99,1.67,2.98,3.77,2.98,6.29c0,1.48-0.41,2.82-1.21,4.01c-0.81,1.2-2.02,2.25-3.65,3.15c1.32,0.33,2.34,0.71,3.03,1.15 c1.14,0.7,2.02,1.63,2.65,2.77c0.63,1.15,0.95,2.51,0.95,4.1c0,2-0.52,3.91-1.56,5.75c-1.05,1.83-2.55,3.24-4.51,4.23 c-1.96,0.99-4.54,1.48-7.74,1.48c-3.11,0-5.57-0.37-7.36-1.1c-1.8-0.73-3.28-1.8-4.44-3.22c-1.16-1.41-2.05-3.19-2.67-5.33 l9.53-1.27c0.38,1.92,0.95,3.26,1.74,4.01c0.78,0.74,1.78,1.12,3,1.12c1.27,0,2.33-0.47,3.18-1.4c0.85-0.93,1.27-2.18,1.27-3.74 c0-1.59-0.41-2.82-1.22-3.69c-0.81-0.87-1.92-1.31-3.32-1.31c-0.74,0-1.77,0.18-3.07,0.56l0.49-6.81c0.52,0.08,0.93,0.12,1.22,0.12 c1.23,0,2.26-0.4,3.08-1.19c0.82-0.79,1.24-1.72,1.24-2.81c0-1.05-0.31-1.88-0.93-2.49c-0.62-0.62-1.48-0.93-2.55-0.93 c-1.12,0-2.02,0.34-2.72,1.01C27.19,7.62,26.72,8.8,26.47,10.49L26.47,10.49z M75.15,8.27l-9.48,1.16 c-0.25-1.32-0.66-2.24-1.24-2.78c-0.59-0.54-1.31-0.81-2.16-0.81c-1.54,0-2.74,0.77-3.59,2.33c-0.62,1.13-1.09,3.52-1.38,7.19 c1.14-1.16,2.31-2.01,3.5-2.56c1.2-0.55,2.59-0.83,4.16-0.83c3.06,0,5.64,1.09,7.75,3.27c2.11,2.19,3.17,4.96,3.17,8.31 c0,2.26-0.53,4.32-1.6,6.2c-1.07,1.87-2.55,3.29-4.44,4.25c-1.9,0.96-4.27,1.44-7.13,1.44c-3.43,0-6.18-0.58-8.25-1.76 c-2.07-1.17-3.73-3.03-4.97-5.59c-1.24-2.56-1.86-5.95-1.86-10.18c0-6.18,1.3-10.71,3.91-13.59C54.13,1.44,57.74,0,62.36,0 c2.73,0,4.88,0.31,6.46,0.94c1.58,0.63,2.9,1.56,3.94,2.76C73.81,4.92,74.61,6.44,75.15,8.27L75.15,8.27z M57.62,23.55 c0,1.86,0.47,3.31,1.4,4.36c0.94,1.05,2.08,1.58,3.44,1.58c1.25,0,2.3-0.48,3.14-1.43c0.84-0.95,1.26-2.37,1.26-4.26 c0-1.93-0.44-3.41-1.31-4.42c-0.88-1.01-1.96-1.52-3.26-1.52c-1.32,0-2.44,0.49-3.34,1.48C58.06,20.32,57.62,21.72,57.62,23.55 L57.62,23.55z M77.91,17.57c0-6.51,1.17-11.07,3.52-13.67C83.77,1.3,87.35,0,92.14,0c2.31,0,4.2,0.29,5.68,0.85 c1.48,0.57,2.69,1.31,3.62,2.22c0.94,0.91,1.68,1.87,2.21,2.87c0.54,1.01,0.97,2.18,1.3,3.52c0.64,2.55,0.96,5.22,0.96,8 c0,6.22-1.05,10.76-3.16,13.64c-2.1,2.88-5.72,4.32-10.87,4.32c-2.88,0-5.21-0.46-6.99-1.38c-1.78-0.92-3.23-2.27-4.37-4.05 c-0.82-1.26-1.47-2.98-1.93-5.17C78.14,22.64,77.91,20.22,77.91,17.57L77.91,17.57z M87.34,17.59c0,4.36,0.38,7.34,1.16,8.94 c0.77,1.6,1.89,2.39,3.36,2.39c0.97,0,1.8-0.34,2.51-1.01c0.71-0.68,1.23-1.76,1.56-3.22c0.34-1.47,0.5-3.75,0.5-6.85 c0-4.55-0.38-7.6-1.16-9.18c-0.77-1.56-1.93-2.35-3.47-2.35c-1.58,0-2.71,0.8-3.42,2.39C87.69,10.31,87.34,13.27,87.34,17.59 L87.34,17.59z M112.14,8.32c0,1.75,0.15,2.94,0.46,3.58c0.31,0.64,0.76,0.96,1.35,0.96c0.39,0,0.72-0.13,1.01-0.41 c0.28-0.27,0.49-0.7,0.63-1.29c0.13-0.59,0.2-1.5,0.2-2.74c0-1.82-0.15-3.05-0.46-3.68c-0.31-0.63-0.77-0.94-1.39-0.94 c-0.63,0-1.09,0.32-1.37,0.96C112.28,5.4,112.14,6.59,112.14,8.32L112.14,8.32z M109.3,30.23c10.56,5.37,8.04,12.99-10.66,17.62 c-5.3,1.31-11.29,2.5-17.86,2.99v6.05c7.31-0.51,14.11-2.19,20.06-3.63c28.12-6.81,27.14-18.97,9.36-25.83 C109.95,28.42,109.65,29.35,109.3,30.23L109.3,30.23z"/>
      </svg>
    </button>
  ` : '';

  // Building icon for stores in shopping complex
  const buildingIcon = store.plaza_cm_customer_count && store.plaza_cm_customer_count > 0 ? `
    <svg stroke="#ea580c" fill="none" stroke-width="2" viewBox="0 0 24 24" height="18" width="18" xmlns="http://www.w3.org/2000/svg" style="display: inline-block; vertical-align: middle;">
      <title>Located in Shopping Complex</title>
      <path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
    </svg>
  ` : '';

  const fullAddress = formatFullAddress({ address: store.address, city: store.city, state: store.state, zipcode: store.zipcode });
  const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(fullAddress)}`;

  // Crime level
  const crimeLabel = store.crime_rate !== null && store.crime_rate !== undefined
    ? (store.crime_rate >= 5000 ? 'Critical' : store.crime_rate >= 1000 ? 'High' : store.crime_rate >= 200 ? 'Medium' : 'Low')
    : null;

  // Build detail rows for the two-column grid
  const detailRows: { label: string; value: string }[] = [];

  detailRows.push({ label: 'Location', value: `${fullAddress} <a href="${googleSearchUrl}" target="_blank" rel="noopener noreferrer" title="Search on Google" style="color: #6b7280; vertical-align: middle;"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: -2px;"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg></a>` });

  if (store.distance) {
    detailRows.push({ label: 'Distance', value: `${store.distance.toFixed(2)} mi` });
  }
  if (store.channel) {
    detailRows.push({ label: 'Channel', value: store.channel?.toLowerCase() === 'diy' ? 'DIY' : store.channel });
  }
  if (store.store_number) {
    detailRows.push({ label: 'Store No', value: store.store_number });
  }
  if (store.id) {
    detailRows.push({ label: 'POI ID', value: String(store.id) });
  }
  if (store.recommendation_rank != null) {
    detailRows.push({ label: 'Rank', value: `#${store.recommendation_rank}` });
  }
  if (crimeLabel) {
    detailRows.push({ label: 'Crime Index', value: crimeLabel });
  }
  if (store.total_crime_amount_usd != null) {
    detailRows.push({ label: 'Total Recovery', value: `$${Math.round(store.total_crime_amount_usd).toLocaleString()}` });
  }
  if (store.video_incident_count != null) {
    detailRows.push({ label: 'Pushout Prevented', value: store.video_incident_count.toLocaleString() });
  }

  // Neighborhood small button (below store name)
  const neighborhoodBtn = showNeighbourhoodButton && store.id ? `
    <a href="/storemap/${store.id}" target="_blank" rel="noopener noreferrer" style="
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      background: #3b82f6;
      color: #fff;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      text-decoration: none;
      cursor: pointer;
    " title="View Neighborhood">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="2" y1="12" x2="22" y2="12"></line>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
      </svg>
      View Neighbourhood
    </a>
  ` : '';

  // Export toggle link (no checkbox, just text)
  const exportToggleLink = showExportToggle && store.id ? `
    <span id="export-toggle-btn-${store.id}" style="
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 11px;
      font-weight: 600;
      color: ${isSelectedForExport ? '#dc2626' : '#3b82f6'};
      cursor: pointer;
      user-select: none;
    ">
      <span>${isSelectedForExport ? 'Remove from Export' : 'Add to Export'}</span>
    </span>
  ` : '';

  // Header
  const headerSection = `
    <div style="padding-bottom: 10px; border-bottom: 2px solid #e5e7eb;">
      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 2px;">
        <div style="font-size: 15px; font-weight: 700; color: #111827; line-height: 1.3;">
          ${store.brand_name || store.site || 'Unknown Store'}${store.store_number ? ` ${store.store_number}` : ''}
        </div>
        ${streetViewBtn}
      </div>
      <div style="display: flex; align-items: center; gap: 6px; font-size: 12px; color: #6b7280; font-weight: 500;">
        ${store.site || ''}
        ${buildingIcon}
      </div>
      ${(exportToggleLink || neighborhoodBtn) ? `<div style="display: flex; align-items: center; gap: 10px; margin-top: 6px;">
        ${exportToggleLink}
        ${neighborhoodBtn}
      </div>` : ''}
    </div>
  `;

  // Details grid (label-value pairs in a compact table)
  const detailsSection = `
    <table style="width: 100%; border-collapse: collapse; margin: 8px 0;">
      ${detailRows.map(row => `
        <tr>
          <td style="padding: 4px 12px 4px 0; font-size: 11px; color: #9ca3af; white-space: nowrap; vertical-align: top; width: 80px;">${row.label}</td>
          <td style="padding: 4px 0; font-size: 12px; color: #374151; line-height: 1.4;">${row.value}</td>
        </tr>
      `).join('')}
    </table>
  `;

  // Solutions section — compact 3-column badge grid
  const hasSolutions = recommendedSolutions.length > 0 || installedSolutions.length > 0 || whitespaceOpportunities.length > 0;

  const badgeStyle = (bg: string) => `display:inline-block;padding:2px 7px;background:${bg};color:#fff;border-radius:3px;font-size:10px;font-weight:500;line-height:1.5;`;

  const solutionsSection = hasSolutions ? `
    <div style="border-top: 1px solid #f3f4f6; padding-top: 8px;">
      <table style="width: 100%; border-collapse: collapse;">
        ${recommendedSolutions.length > 0 ? `
        <tr>
          <td style="padding: 3px 8px 3px 0; font-size: 10px; color: #9ca3af; white-space: nowrap; vertical-align: top; width: 75px;">Recommended</td>
          <td style="padding: 3px 0;">
            <div style="display: flex; gap: 4px; flex-wrap: wrap;">
              ${recommendedSolutions.map(sol => `<span style="${badgeStyle('#3b82f6')}">${sol}</span>`).join('')}
            </div>
          </td>
        </tr>
        ` : ''}
        ${installedSolutions.length > 0 ? `
        <tr>
          <td style="padding: 3px 8px 3px 0; font-size: 10px; color: #9ca3af; white-space: nowrap; vertical-align: top; width: 75px;">Installed</td>
          <td style="padding: 3px 0;">
            <div style="display: flex; gap: 4px; flex-wrap: wrap;">
              ${installedSolutions.map(item => `<span style="${badgeStyle('#10b981')}">${item.name}</span>`).join('')}
            </div>
          </td>
        </tr>
        ` : ''}
        ${whitespaceOpportunities.length > 0 ? `
        <tr>
          <td style="padding: 3px 8px 3px 0; font-size: 10px; color: #9ca3af; white-space: nowrap; vertical-align: top; width: 75px;">Whitespace</td>
          <td style="padding: 3px 0;">
            <div style="display: flex; gap: 4px; flex-wrap: wrap;">
              ${whitespaceOpportunities.map(opp => `<span style="${badgeStyle('#f59e0b')}">${opp}</span>`).join('')}
            </div>
          </td>
        </tr>
        ` : ''}
      </table>
    </div>
  ` : '';

  return `
    <div style="
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      width: 360px;
      color: #1f2937;
    ">
      ${headerSection}
      ${detailsSection}
      ${solutionsSection}
    </div>
  `;
}
